import React from "react";
import { MdOutlineCloudDownload } from "react-icons/md";
import { toast } from "react-hot-toast";
import { BiPlus, BiChevronLeft, BiChevronRight } from "react-icons/bi";
import Layout from "../Layout";
import { Button } from "../components/Form";
import { DoctorsTable } from "../components/Tables";
import AddStaffModal from "../components/Modals/AddStaffModal";
import { BaseUrl } from "../BaseUrl";
import axios from "axios";

function Receptions() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [data, setData] = React.useState({});
  const [staffUsers, setStaffUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalUsers, setTotalUsers] = React.useState(0);
  const itemsPerPage = 4; // Hardcoded for testing as per user request

  // Fetch staff users (role 2 OR 3) from API
  const fetchStaffUsers = React.useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${BaseUrl}/facility/getAllStaffUsers?page=${page}&limit=${itemsPerPage}`,
          {
            withCredentials: true,
          }
        );

        if (response.data.success && response.data.data) {
          // Transform API data to match DoctorsTable format
          const transformedData = response.data.data.map((user, index) => {
            // Generate avatar with first letter of name if no profile image
            const fullName = user.fullName || "Unknown";
            const nameParts = fullName.split(" ");
            const firstName = nameParts[0] || "";
            const lastName = nameParts[1] || "";
            const avatarUrl =
              user.userImage ||
              user.doctorImage ||
              `https://api.dicebear.com/5.x/initials/svg?seed=${firstName}_${lastName}`;

            return {
              id: user._id || index + 1,
              _id: user._id,
              user: {
                image: avatarUrl,
                title: fullName,
                phone: user.mobileNumber || "N/A",
                email: user.emailId || "N/A",
              },
              title:
                user.category === "Doctor" ? "Dr." : user.category || "Staff",
              role: user.role, // Store role for reference
              category: user.category,
              hprId: user.hprId,
              dateOfBirth: user.dateOfBirth,
              gender: user.gender,
              createdAt: user.createdAt, // Include createdAt for date formatting
              joinedAt: user.createdAt, // Also include as joinedAt
              fullName: user.fullName,
              mobileNumber: user.mobileNumber,
              emailId: user.emailId,
            };
          });
          setStaffUsers(transformedData);

          // Update pagination info if available
          if (response.data.totalPages) {
            setTotalPages(response.data.totalPages);
          }
          if (response.data.totalUsers) {
            setTotalUsers(response.data.totalUsers);
          }
        } else {
          toast.error("Failed to fetch staff users");
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Error loading staff users"
        );
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage]
  );

  React.useEffect(() => {
    fetchStaffUsers(currentPage);
  }, [currentPage, fetchStaffUsers]);

  const onCloseModal = () => {
    setIsOpen(false);
    setData({});
    // Refresh the list after modal closes
    fetchStaffUsers(currentPage);
  };

  const preview = (data) => {
    setIsOpen(true);
    setData(data);
  };

  const handleDelete = async (staffData) => {
    try {
      const response = await axios.delete(
        `${BaseUrl}/facility/deleteStaff/${staffData._id || staffData.id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        const name =
          staffData.fullName || staffData.user?.title || "staff member";
        toast.success(`Deleted ${name}`);
        // Refresh the list
        fetchStaffUsers(currentPage);
      } else {
        toast.error(response.data.message || "Failed to delete staff member");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete staff member"
      );
    }
  };

  // Filter staff users based on search query
  const filteredStaffUsers = staffUsers.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.user.title.toLowerCase().includes(searchLower) ||
      user.user.email.toLowerCase().includes(searchLower) ||
      user.user.phone.toLowerCase().includes(searchLower) ||
      (user.hprId && user.hprId.toLowerCase().includes(searchLower))
    );
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Export to CSV function
  const handleExport = () => {
    try {
      // Use filtered or all staff users
      const dataToExport =
        filteredStaffUsers.length > 0 ? filteredStaffUsers : staffUsers;

      if (dataToExport.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Create CSV headers
      const headers = ["#", "Name", "Joined At", "Phone", "Access", "Email ID"];

      // Create CSV rows
      const csvRows = [
        headers.join(","),
        ...dataToExport.map((user, index) => {
          const accessLabel =
            user.role === "2"
              ? "Admin"
              : user.role === "3"
                ? "Manager"
                : "Member";

          const joinedDate = formatDate(user.createdAt || user.joinedAt);

          return [
            index + 1,
            `"${user.user.title}"`,
            `"${joinedDate}"`,
            `"${user.user.phone}"`,
            `"${accessLabel}"`,
            `"${user.user.email}"`,
          ].join(",");
        }),
      ];

      // Create CSV content
      const csvContent = csvRows.join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `staff_members_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Staff data exported successfully!");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  // Helper function to format date for export
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(
    currentPage * itemsPerPage,
    totalUsers || filteredStaffUsers.length
  );

  return (
    <Layout>
      {/* Add Staff Modal */}
      {isOpen && (
        <AddStaffModal closeModal={onCloseModal} isOpen={isOpen} datas={data} />
      )}

      <h1 className='text-xl font-semibold'>Member & Access Management</h1>
      <div
        data-aos='fade-up'
        data-aos-duration='1000'
        data-aos-delay='100'
        data-aos-offset='200'
        className='my-8 rounded-xl border-[1px] border-border bg-white p-5'
      >
        {/* Search and Action Buttons */}
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-6'>
          <div className='grid items-center gap-6 md:col-span-4 lg:grid-cols-4'>
            <input
              type='text'
              placeholder='Search staff by name...'
              className='h-14 w-full rounded-md border border-border bg-dry px-4 text-sm text-main'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Add Staff Button */}
          <Button
            label='Add Staff'
            Icon={BiPlus}
            onClick={() => {
              setData({});
              setIsOpen(true);
            }}
          />

          {/* Export Button */}
          <Button
            label='Export'
            Icon={MdOutlineCloudDownload}
            onClick={() => {
              handleExport();
            }}
          />
        </div>

        {/* Table */}
        <div className='mt-8 w-full overflow-x-scroll'>
          {loading ? (
            <div className='flex items-center justify-center py-10'>
              <p className='text-textGray'>Loading staff users...</p>
            </div>
          ) : filteredStaffUsers.length === 0 ? (
            <div className='flex items-center justify-center py-10'>
              <p className='text-textGray'>
                {searchQuery
                  ? "No staff users found matching your search."
                  : "No staff users found."}
              </p>
            </div>
          ) : (
            <>
              <DoctorsTable
                doctor={false}
                data={filteredStaffUsers}
                functions={{
                  preview: preview,
                  delete: handleDelete,
                }}
              />

              {/* Pagination */}
              <div className='mt-6 flex items-center justify-between'>
                <p className='text-sm text-textGray'>
                  Showing {startIndex}-{endIndex} of{" "}
                  {totalUsers || filteredStaffUsers.length} staff members
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex h-10 w-10 items-center justify-center rounded-md border ${
                      currentPage === 1
                        ? "cursor-not-allowed border-gray-300 text-gray-300"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    <BiChevronLeft className='text-xl' />
                  </button>
                  <span className='rounded-md bg-subMain bg-opacity-10 px-4 py-2 text-sm font-medium text-main'>
                    {currentPage}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className={`flex h-10 w-10 items-center justify-center rounded-md border ${
                      currentPage >= totalPages
                        ? "cursor-not-allowed border-gray-300 text-gray-300"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    <BiChevronRight className='text-xl' />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Receptions;
